import React, {
	Children,
	ComponentType,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { OverviewEngineSelectors } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { UiStateSelector } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/store";
import { OverviewEngineApi } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/api";
import { FilterBar } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/filter-bar";
import { FilterButton } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/filter-button";
import { Filter } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/filter-options-view";
import { FilterSelector } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/filter-selector";
import { EnumerationFilterOptionsView } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/options-views/enumeration-filter-options-view";
import { StringFilterOptionsView } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/options-views/string-filter-options-view";
import { DefaultComponentMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import { DefaultWidgetMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/widget-map";
import {
	FilterContext,
	useFilterContext
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/context/filter-context";
import { useOverviewContentBoxContext } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/context/overview-content-box-context";
import { useOverviewEngineState } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/context/overview-engine-context";
import { FilterProps, FilterSelectorProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/faceted-search";
import { FilterSelectorMobileProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/faceted-search/main/filter-selector/filter-selector.mobile.api";

import {
	getFVManifest,
	getFVManifestMap,
	getFVManifestVersionsById
} from "../../../app/slices/fachverfahren/selectors";
import { FachverfahrenSlice } from "../../../app/slices/fachverfahren/types";
import { loadAssigneeCandidates } from "../../../app/slices/users/actions";
import { getUsersForFV } from "../../../app/slices/users/selectors";
import { AssigneeCandidate } from "../../../app/slices/users/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { assertNonNullable } from "../../../utilities/assert";
import {
	PATH_TO_ASSIGNEE_FULL_NAME,
	PATH_TO_ERSTELLT_IN_VERSION,
	PATH_TO_STATUS,
	QUERY_PATH_TO_ASSIGNEE_FULL_NAME,
	QUERY_PATH_TO_ASSIGNEE_ID,
	QUERY_PATH_TO_ERSTELLT_IN_VERSION,
	MODEL_PATH_TO_STATUS
} from "../../../utilities/variables/documentModelPaths";
import { GeneralOverviewWidgetsMap } from "../../generalOverviewEngine/GeneralOverviewEngine";

import EnumerationOptions = OverviewEngineApi.Filter.EnumerationOptions;
import EnumerationOption = EnumerationFilterOptionsView.EnumerationOption;

function formatUsername(username: string, candidates: readonly AssigneeCandidate[]): string {
	const assigneeCandidate = candidates?.find(candidate => candidate.username === username);
	return assigneeCandidate ? `${assigneeCandidate.firstName} ${assigneeCandidate.lastName}` : "";
}

export const CustomFilter: ComponentType<FilterProps> = function CustomFilter(props) {
	const { options, id } = props;
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);
	assertNonNullable(id);
	const selectedFilter = useSelector(OverviewEngineSelectors.uiState(activityId))?.activeFilters?.[id];
	const acitivty = useSelector(ActivitySelectors.activityById(activityId));
	const manifest = useSelector(getFVManifest(acitivty?.descriptor?.fachverfahren));
	const candidates = useSelector(getUsersForFV(manifest));

	const statusOptions = useMemo(() => {
		if (!options || MODEL_PATH_TO_STATUS !== id) {
			return options;
		}
		const newOptions = Children.toArray(options).flatMap(it => {
			if (typeof it === "string") {
				return [it];
			}
			if (typeof it === "object" && "props" in it) {
				const element = it as React.ReactElement<{ children?: React.ReactNode }>;
				return Children.toArray(element.props.children).filter(
					(child): child is string => typeof child === "string"
				);
			}
			return [];
		});
		return newOptions.map(it => {
			if (typeof it !== "string") {
				return it;
			}

			return it
				.split(",")
				.map(itt => itt.replace(/[[a-zA-Z0-9_]+]$/, ""))
				.join(",");
		});
	}, [options, id]);

	if (selectedFilter && EnumerationOptions.isInstance(selectedFilter) && QUERY_PATH_TO_ASSIGNEE_FULL_NAME === id) {
		return (
			<DefaultWidgetMap.Filter
				{...props}
				options={selectedFilter.criteria?.selectedValues.map(it => formatUsername(it, candidates ?? []))}
				id={QUERY_PATH_TO_ASSIGNEE_ID}
				name={"Zugewiesen an"}
			/>
		);
	} else if (
		selectedFilter &&
		EnumerationOptions.isInstance(selectedFilter) &&
		QUERY_PATH_TO_ERSTELLT_IN_VERSION === id
	) {
		// StringType field gets EnumerationOptions here, so the library can't derive a label
		return <DefaultWidgetMap.Filter {...props} options={selectedFilter.criteria?.selectedValues} />;
	} else if (MODEL_PATH_TO_STATUS !== id || options == null) {
		return <DefaultWidgetMap.Filter {...props} />;
	}
	return <DefaultWidgetMap.Filter {...props} options={statusOptions} />;
};

export const CustomFilterButton: ComponentType<FilterButton.PropsType> = function CustomFilterButton({
	onClick,
	...props
}) {
	const showFilterSelector = useOverviewContentBoxContext(context => context.showFilterSelector);

	const activeFilters = useOverviewEngineState(UiStateSelector.activeFilters());
	const isAnyRelevantFilterActive = Object.values(activeFilters ?? {}).some(id => !id?.nonRemovable);

	const onFilterSelectorVisibilityChange = useOverviewContentBoxContext(
		context => context.onFilterSelectorVisibilityChange
	);

	const onFilterButtonClick = useCallback(() => {
		if (!isAnyRelevantFilterActive) {
			onFilterSelectorVisibilityChange(!showFilterSelector);
		} else {
			onClick();
		}
	}, [isAnyRelevantFilterActive, showFilterSelector, onFilterSelectorVisibilityChange, onClick]);

	return (
		<DefaultComponentMap.FilterButton
			{...props}
			onClick={onFilterButtonClick}
			showBadge={props.showBadge && isAnyRelevantFilterActive}
		/>
	);
};

export const CustomFilterBar: ComponentType<FilterBar.Props> = function CustomFilterBar(props) {
	// Synthetic filters only apply to a single Fachverfahren's task list, not other overviews
	const isTaskListView = useIsTaskListView();
	const storeActiveFilters = useStoreActiveFilters();
	const customActiveFilters = useMemo(
		() => (isTaskListView ? buildCustomActiveFilters(storeActiveFilters) : []),
		[isTaskListView, storeActiveFilters]
	);
	const activeFilters = useMemo(
		() => [
			...(isTaskListView
				? excludeFiltersReplacedByCustomOnes(props.activeFilters.filter(item => !item.nonRemovable))
				: props.activeFilters.filter(item => !item.nonRemovable)),
			...customActiveFilters
		],
		[isTaskListView, props.activeFilters, customActiveFilters]
	) as Filter.FilterData[];

	// useContext(FilterContext) is stale; use the library's own selector hook
	const contextFilters = useFilterContext();
	const mergedContextFilters = useMemo(
		() => (isTaskListView ? addEnhancedFilters(contextFilters, storeActiveFilters) : contextFilters),
		[isTaskListView, contextFilters, storeActiveFilters]
	);

	return (
		<FilterContext.Provider value={{ filters: mergedContextFilters }}>
			<DefaultComponentMap.FilterBar {...props} activeFilters={activeFilters} />
		</FilterContext.Provider>
	);
};

export const CustomFilterSelectorMobile: ComponentType<FilterSelectorMobileProps> = function CustomFilterSelectorMobile(
	props
) {
	const isTaskListView = useIsTaskListView();
	const customActiveFilters = useCustomActiveFilters();
	const activeFilters = useMemo(
		() => [
			...(isTaskListView
				? excludeFiltersReplacedByCustomOnes(props.activeFilters.filter(item => !item.nonRemovable))
				: props.activeFilters.filter(item => !item.nonRemovable)),
			...(isTaskListView ? customActiveFilters : [])
		],
		[props.activeFilters, isTaskListView, customActiveFilters]
	);
	return <GeneralOverviewWidgetsMap.FilterSelectorMobile {...props} activeFilters={activeFilters} />;
};

export const CustomFilterSelectorWidget: ComponentType<FilterSelectorProps> = function CustomFilterSelector(props) {
	const activeFilters = useMemo(() => props.activeFilters.filter(item => !item.nonRemovable), [props.activeFilters]);
	return <GeneralOverviewWidgetsMap.FilterSelector {...props} activeFilters={activeFilters} />;
};

export const CustomFilterSelector: ComponentType<FilterSelector.Props> = createActivityWrapperComponent(
	function CustomFilterSelector(props) {
		const activity = useSelector(ActivitySelectors.activityById(props.activityId));
		const istTaskList = activity?.descriptor.view === "TaskList";
		// Live UI state, not the server-echoed slices which can drop values
		const storeActiveFilters = useSelector(OverviewEngineSelectors.uiState(props.activityId))?.activeFilters;

		const enhancedFilters: Filter.FilterData[] = useMemo(() => {
			if (!istTaskList) {
				return props.filters;
			}

			return addEnhancedFilters(props.filters, storeActiveFilters);
		}, [istTaskList, storeActiveFilters, props.filters]);

		// FilterSelector freezes state at mount; force remount on change
		const filterSelectorKey = useFilterStateKey(enhancedFilters);

		return <DefaultComponentMap.FilterSelector key={filterSelectorKey} {...props} filters={enhancedFilters} />;
	}
);

/**
 * Derives a key that changes only when a filter's id/active/filterOptions change, to force a remount.
 */
function useFilterStateKey(filters: Filter.FilterData[]): string {
	return useMemo(() => computeFilterStateKey(filters), [filters]);
}

/**
 * Pure part of useFilterStateKey, extracted for testability.
 */
export function computeFilterStateKey(filters: Filter.FilterData[]): string {
	return JSON.stringify(filters.map(({ id, active, filterOptions }) => ({ id, active, filterOptions })));
}

/**
 * Synthetic filters with no corresponding filterable document model element (see buildCustomActiveFilters).
 */
const CUSTOM_FILTER_DEFINITIONS: readonly { id: string; label: string; path: ModelPath }[] = [
	{
		id: QUERY_PATH_TO_ERSTELLT_IN_VERSION,
		label: "Erstellt in Version",
		path: PATH_TO_ERSTELLT_IN_VERSION
	},
	{
		id: QUERY_PATH_TO_ASSIGNEE_FULL_NAME,
		label: "Zugewiesen an",
		path: PATH_TO_ASSIGNEE_FULL_NAME
	}
];

function addEnhancedFilters(
	existingFilters: Filter.FilterData[],
	fieldBasedFilters?: OverviewEngineApi.FilterMap
): Filter.FilterData[] {
	const customFilters = CUSTOM_FILTER_DEFINITIONS.map(it => ({
		...it,
		active: !!fieldBasedFilters?.[it.id],
		filterOptions: fieldBasedFilters?.[it.id]
	}));

	return [...excludeFiltersReplacedByCustomOnes(existingFilters), ...customFilters];
}

/**
 * Removes filters replaced by a synthetic filter
 */
export function excludeFiltersReplacedByCustomOnes<T extends { id: string; label: ReactNode }>(filters: T[]): T[] {
	return filters.filter(
		it =>
			!CUSTOM_FILTER_DEFINITIONS.some(
				customFilter => it.id === customFilter.id || it.label === customFilter.label
			)
	);
}

/**
 * Resolves the id (slash-separated model path, matching CUSTOM_FILTER_DEFINITIONS / all other filter
 * maps in this module) of the synthetic filter for the given model path, if any.
 */
export function getSyntheticFilterId(path: ModelPath): string | undefined {
	return CUSTOM_FILTER_DEFINITIONS.find(definition => comparePaths(definition.path, path))?.id;
}

/**
 * Builds active Filter.FilterData for the synthetic filters, since the FilterBar otherwise
 * never shows them (see CUSTOM_FILTER_DEFINITIONS).
 */
export function buildCustomActiveFilters(storeActiveFilters?: OverviewEngineApi.FilterMap): Filter.FilterData[] {
	return CUSTOM_FILTER_DEFINITIONS.filter(({ id }) => !!storeActiveFilters?.[id]).map(definition => ({
		...definition,
		active: true,
		filterOptions: storeActiveFilters?.[definition.id]
	}));
}

function useStoreActiveFilters(): OverviewEngineApi.FilterMap | undefined {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	return useSelector(OverviewEngineSelectors.uiState(activityId))?.activeFilters;
}

/**
 * The synthetic filters (see CUSTOM_FILTER_DEFINITIONS) only apply to the process task list of a
 * single Fachverfahren (mirrors the check already used by CustomFilterSelector).
 */
function useIsTaskListView(): boolean {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	return (
		useSelector(
			ActivitySelectors.activityPropById(activityId, activity => activity?.descriptor.view === "TaskList")
		) ?? false
	);
}

function useCustomActiveFilters(): Filter.FilterData[] {
	const storeActiveFilters = useStoreActiveFilters();

	return useMemo(() => buildCustomActiveFilters(storeActiveFilters), [storeActiveFilters]);
}

export const CustomStringFilterOptionsView: ComponentType<StringFilterOptionsView.Props> =
	createActivityWrapperComponent(function CustomStringFilterOptionsView(props) {
		const [selectedValues, setSelectedValues] = useState<string[]>([]);
		const fvId = useSelector(ActivitySelectors.activityPropById(props.activityId, a => a.descriptor.fachverfahren));
		const manifest = useSelector(getFVManifest(fvId));

		// Live UI state, not the server-echoed slices which can drop values
		const storeActiveFilters = useSelector(OverviewEngineSelectors.uiState(props.activityId))?.activeFilters;
		const allVersions = useSelector(getFVManifestVersionsById(fvId ?? ""));
		const userCandidates = useSelector(getUsersForFV(manifest));

		const dispatch = useDispatch();

		useEffect(() => {
			if (manifest && !userCandidates) {
				// Lazy fetch assignee candidates from server
				dispatch(loadAssigneeCandidates({ fvKey: manifest.identification.key }));
			}
		}, [userCandidates, dispatch, manifest]);

		useEffect(() => {
			// activeFilters is keyed by the synthetic filter id (slash path), not a dot-joined path
			const filterId = getSyntheticFilterId(props.path);
			const filterOptions = filterId ? storeActiveFilters?.[filterId] : undefined;

			if (filterOptions && OverviewEngineApi.Filter.EnumerationOptions.isInstance(filterOptions)) {
				setSelectedValues(filterOptions.criteria?.selectedValues ?? []);
			}
		}, [storeActiveFilters, props.path]);

		const enumerationOptions = useMemo(() => {
			if (comparePaths(PATH_TO_ERSTELLT_IN_VERSION, props.path)) {
				return allVersions
					.map(version => version.toString())
					.map(entry => ({
						checked: selectedValues.includes(entry),
						label: entry,
						value: entry,
						active: selectedValues.includes(entry)
					}));
			} else if (comparePaths(PATH_TO_ASSIGNEE_FULL_NAME, props.path)) {
				return [...(userCandidates ?? [])]
					.sort((a, b) => (a.fullName ?? a.username).localeCompare(b.fullName ?? b.username))
					.map(candidate => ({
						checked: selectedValues.includes(candidate.username),
						label: `${candidate.firstName} ${candidate.lastName}`,
						value: candidate.username,
						active: selectedValues.includes(candidate.username)
					}));
			}
			return [];
		}, [userCandidates, allVersions, selectedValues, props.path]);

		const onChangeHandler = useCallback(
			(options: OverviewEngineApi.Filter.Options) => {
				if (!OverviewEngineApi.Filter.EnumerationOptions.isInstance(options) || !props.onChange) {
					return;
				}

				setSelectedValues(options.criteria?.selectedValues ?? []);
				props.onChange(options);
			},
			[props]
		);

		if (
			comparePaths(PATH_TO_ERSTELLT_IN_VERSION, props.path) ||
			comparePaths(PATH_TO_ASSIGNEE_FULL_NAME, props.path)
		) {
			return (
				<DefaultComponentMap.FilterOptionsViews.EnumerationFilterOptionsView
					{...props}
					onChange={onChangeHandler}
					enumerationOptions={enumerationOptions}
				/>
			);
		} else {
			return <DefaultComponentMap.FilterOptionsViews.StringFilterOptionsView {...props} />;
		}
	});

function createActivityWrapperComponent<T>(
	Component: ComponentType<T & { readonly activityId: string }>
): ComponentType<T> {
	return function RowWrapper(props) {
		const activityId = useContext(ActivityIdContext);
		assertNonNullable(activityId);

		return <Component {...props} activityId={activityId} />;
	};
}

const GLOBAL_STATUS = ["NEU", "ARCHIVIERT", "IN_VERARBEITUNG"];

function allowedFachverfahrenStatus(
	fachverfahrenManifestMap: FachverfahrenSlice["manifests"]
): (option: EnumerationOption) => boolean {
	return function isAllowedFachverfahrenStatus({ label, value }) {
		const splitStatus = value.split("_");
		return (
			GLOBAL_STATUS.includes(value) ||
			!label.endsWith(`[${splitStatus.slice(0, 2).join("_")}]`) ||
			!!fachverfahrenManifestMap[value.split("_")[0] ?? ""]
		);
	};
}

export const CustomEnumerationFilterOptionsView: ComponentType<EnumerationFilterOptionsView.Props> = function (props) {
	const { path, enumerationOptions } = props;
	//EG: [t0_HF1]
	const technicalProcessStateSuffix = /[[a-zA-Z0-9_]+]$/;

	const fachverfahrenManifestMap = useSelector(getFVManifestMap);

	const statusEnumerationFilterOptions = useMemo(() => {
		if (!comparePaths(PATH_TO_STATUS, path)) {
			return enumerationOptions;
		}

		return enumerationOptions
			.filter(allowedFachverfahrenStatus(fachverfahrenManifestMap))
			.map(it => ({ ...it, label: it.label.replace(technicalProcessStateSuffix, "") }))
			.filter(filterDistinctKey(it => it.label))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [enumerationOptions, path, technicalProcessStateSuffix, fachverfahrenManifestMap]);

	if (!comparePaths(PATH_TO_STATUS, path)) {
		return <DefaultComponentMap.FilterOptionsViews.EnumerationFilterOptionsView {...props} />;
	}

	return (
		<DefaultComponentMap.FilterOptionsViews.EnumerationFilterOptionsView
			{...props}
			enumerationOptions={statusEnumerationFilterOptions}
		/>
	);
};

function comparePaths(path1: ModelPath, path2: ModelPath): boolean {
	return path1.length === path2.length && path1.every((entry, i) => entry.elementName === path2[i].elementName);
}

function filterDistinctKey<T, S>(keyExtractor: (it: T) => S): (obj: T) => boolean {
	const seenKeys = new Set<S>();
	return (obj: T) => {
		const key = keyExtractor(obj);
		if (seenKeys.has(key)) {
			return false;
		}
		seenKeys.add(key);
		return true;
	};
}
